import React from 'react';

/**
 * Luxury LoadingBuffer component
 * Provides a centered, royal-themed buffering spinner with animations
 * matching Vanya's royal purple and champagne gold design.
 */
const LoadingBuffer = ({
  message = "Loading Royal Handloom Collection...",
  tag = "✦ VANYA COLLECTIONS ✦",
  minHeight = "60vh",
  fullPage = false,
  style = {}
}) => {
  return (
    <div
      className="vanya-buffer-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        minHeight: fullPage ? '80vh' : minHeight,
        padding: '40px 20px',
        boxSizing: 'border-box',
        textAlign: 'center',
        ...style
      }}
    >
      {/* Dual Ring Spinner with Center Sparkle */}
      <div className="vanya-buffer-spinner-wrapper">
        <div className="vanya-buffer-outer-ring" />
        <div className="vanya-buffer-inner-ring" />
        <div className="vanya-buffer-center-sparkle">✦</div>
      </div>

      {/* Brand Tag */}
      {tag && (
        <span className="vanya-buffer-tag">
          {tag}
        </span>
      )}

      {/* Main Message */}
      <p className="vanya-buffer-text">
        {message}
      </p>

      {/* Shimmering Progress Bar */}
      <div className="vanya-buffer-progress-bar">
        <div className="vanya-buffer-progress-shimmer" />
      </div>

      <style>{`
        .vanya-buffer-spinner-wrapper {
          position: relative;
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 22px;
        }

        .vanya-buffer-outer-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 3px solid rgba(226, 188, 83, 0.2);
          border-top-color: #e2bc53;
          border-right-color: #fbeea4;
          animation: bufferSpin 1.1s cubic-bezier(0.5, 0.1, 0.5, 0.9) infinite;
          box-shadow: 0 0 15px rgba(226, 188, 83, 0.25);
        }

        .vanya-buffer-inner-ring {
          position: absolute;
          inset: 8px;
          border-radius: 50%;
          border: 2px dashed rgba(82, 43, 91, 0.35);
          border-bottom: 2px solid #522b5b;
          animation: bufferSpinReverse 1.6s linear infinite;
        }

        .vanya-buffer-center-sparkle {
          color: #e2bc53;
          font-size: 15px;
          animation: bufferPulse 1.8s ease-in-out infinite;
          text-shadow: 0 0 10px rgba(226, 188, 83, 0.7);
          line-height: 1;
        }

        .vanya-buffer-tag {
          font-family: 'Montserrat', sans-serif;
          color: #c99b27;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 3.5px;
          text-transform: uppercase;
          margin-bottom: 8px;
          animation: bufferFade 2s ease-in-out infinite alternate;
        }

        .vanya-buffer-text {
          font-family: 'Playfair Display', serif;
          color: #3b1443;
          font-size: clamp(16px, 2.5vw, 19px);
          font-weight: 600;
          letter-spacing: 0.5px;
          margin: 0 0 16px;
          line-height: 1.4;
          max-width: 480px;
        }

        .vanya-buffer-progress-bar {
          width: 140px;
          height: 3px;
          background: rgba(82, 43, 91, 0.12);
          border-radius: 999px;
          overflow: hidden;
          position: relative;
        }

        .vanya-buffer-progress-shimmer {
          position: absolute;
          top: 0;
          bottom: 0;
          left: -100%;
          width: 100%;
          background: linear-gradient(90deg, transparent, #e2bc53, #fbeea4, transparent);
          border-radius: 999px;
          animation: bufferShimmer 1.8s ease-in-out infinite;
        }

        @keyframes bufferSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes bufferSpinReverse {
          0% { transform: rotate(360deg); }
          100% { transform: rotate(0deg); }
        }

        @keyframes bufferPulse {
          0%, 100% { transform: scale(0.9); opacity: 0.7; }
          50% { transform: scale(1.25); opacity: 1; }
        }

        @keyframes bufferFade {
          0% { opacity: 0.65; }
          100% { opacity: 1; }
        }

        @keyframes bufferShimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
      `}</style>
    </div>
  );
};

export default LoadingBuffer;
